import { IAlertLoudness, IAlertStatus, IAlertDelivery, IStatus } from "../Types"
import { Native } from "./Native"

// 2001-01-01T00:00:00Z expressed in Unix epoch seconds. Swift's default
// `JSONEncoder` serialises a `Date` as `timeIntervalSinceReferenceDate` (seconds
// since that instant), which is how the persisted `_episode` dates are encoded.
const SWIFT_REFERENCE_EPOCH_SECONDS = 978307200;

/** Convert a Swift `Date` (secondsSinceReferenceDate) to Unix epoch milliseconds. */
export function swiftDateToEpochMs(seconds: number): number {
  return (seconds + SWIFT_REFERENCE_EPOCH_SECONDS) * 1000;
}

/**
 * How a device should present an open alert this tick — the TS port of the Swift
 * `AlertDeliveryAction` enum (`Sources/LogbookModel/Status/AlertDelivery.swift`).
 * Kept in sync by parity tests.
 */
export enum AlertDeliveryAction {
  silent = "silent",
  notify = "notify",
  alarm = "alarm",
  stop = "stop",
}

/**
 * The subset of the persisted `AlertEpisode` (nested under `_episode`) the client
 * delivery decision reads. `AlertEpisode` is not code-generated into TS, so the
 * shape is declared here; dates are Swift reference-date seconds.
 */
interface DeliveryEpisode {
  status?: number;
  snoozeUntil?: number | null;
  acknowledgedBy?: number | null;
  loudness?: number;
}

interface AlertEnvelope {
  _delivery?: IAlertDelivery;
  _episode?: DeliveryEpisode;
}

/**
 * The canonical, pure delivery decision — the TS port of Swift
 * `AlertDelivery.action(...)`. Given the live episode state decide how this
 * device should present the alert. Times are Unix epoch milliseconds.
 */
export function deliveryAction(p: {
  status: number;
  loudness: number;
  acknowledged: boolean;
  snoozeUntilMs?: number | null;
  targeted: boolean;
  nowMs: number;
}): AlertDeliveryAction {
  if (!p.targeted) return AlertDeliveryAction.silent;
  if (p.status === IAlertStatus.resolved) return AlertDeliveryAction.stop;
  const snoozed = p.snoozeUntilMs != null && p.nowMs < p.snoozeUntilMs;
  if (
    p.status === IAlertStatus.alarm &&
    p.loudness === IAlertLoudness.audible &&
    !p.acknowledged &&
    !snoozed
  ) {
    return AlertDeliveryAction.alarm;
  }
  return AlertDeliveryAction.notify;
}

function decodeEnvelope(data: string | undefined): AlertEnvelope {
  if (!data) return {};
  try {
    return JSON.parse(data) as AlertEnvelope;
  } catch {
    return {};
  }
}

/**
 * Decide how the given alert `Status` should be presented on `userId`'s device,
 * decoding the persisted `_delivery` / `_episode` envelope. An alert with no
 * `_delivery` descriptor (an alert predating #898, tracker #873) is treated as an all-hands fan-out.
 *
 * `isPrimary` marks the ship's primary device, which is an unconditional fallback
 * recipient of every open alert regardless of the `_delivery` audience — so the
 * boat's alarm host can never be routed to nobody (an on-watch `users` audience may
 * hold only unaccounted ship users or offline companions, and the primary may run
 * without a user identity). Loudness still governs sound-vs-notify.
 */
export function decideDelivery(
  alert: IStatus,
  userId: number | undefined,
  nowMs: number,
  isPrimary = false,
): AlertDeliveryAction {
  const env = decodeEnvelope(alert.data);
  const delivery = env._delivery;
  const episode = env._episode;
  const targeted = isPrimary
    ? true
    : delivery
      ? delivery.all || (userId != null && delivery.users.includes(userId))
      : true;
  const loudness = delivery?.loudness ?? episode?.loudness ?? IAlertLoudness.audible;
  const status = episode?.status ?? alert.value;
  const acknowledged = episode?.acknowledgedBy != null;
  const snoozeUntilMs =
    episode?.snoozeUntil != null ? swiftDateToEpochMs(episode.snoozeUntil) : null;
  return deliveryAction({ status, loudness, acknowledged, snoozeUntilMs, targeted, nowMs });
}

/** How to describe an alert for a native OS notification. */
export interface AlertDescriptor {
  title: string;
  body: string;
}

/**
 * The client-side delivery consumer. Given the set of currently-open alert
 * `Status`es each tick, it drives the native bridge: sounds / stops the audible
 * alarm and posts / suppresses notifications, tracking which alerts are already
 * sounding / notified so repeated ticks are idempotent. Membership is decided per
 * device from the persisted envelope against `userId` (with the primary device an
 * unconditional fallback recipient — see `decideDelivery`). This is the JS mirror
 * of the native primary's own delivery — it lets any device (including a plain
 * browser) present an alert from the persisted envelope alone.
 */
export class AlertDeliveryConsumer {
  private sounding = new Set<number>();
  private notified = new Set<number>();
  private receipted = new Set<number>();

  reconcile(
    alerts: IStatus[],
    userId: number | undefined,
    nowMs: number,
    describe: (alert: IStatus) => AlertDescriptor,
    isPrimary = false,
    onReceipt?: (alert: IStatus) => void,
  ): void {
    const open = new Set(alerts.map((a) => a.id));

    // Silence and forget any alert that is no longer open (cleared / resolved).
    for (const id of Array.from(this.sounding)) {
      if (!open.has(id)) {
        Native.stopAlarm(String(id));
        this.sounding.delete(id);
      }
    }
    for (const id of Array.from(this.notified)) {
      if (!open.has(id)) this.notified.delete(id);
    }
    for (const id of Array.from(this.receipted)) {
      if (!open.has(id)) this.receipted.delete(id);
    }

    for (const alert of alerts) {
      const id = alert.id;
      const action = decideDelivery(alert, userId, nowMs, isPrimary);
      switch (action) {
        case AlertDeliveryAction.alarm:
          this.notifyOnce(id, describe(alert));
          if (!this.sounding.has(id)) {
            Native.playAlarm(String(id));
            this.sounding.add(id);
          }
          // Tell the primary this device delivered the alert locally (over the
          // LAN) so the cloud never sends a redundant remote push. Only an
          // audible alarm delivery counts — a quiet notify must not suppress a
          // later push if the alert escalates. Fire once per open episode.
          if (onReceipt && !this.receipted.has(id)) {
            this.receipted.add(id);
            onReceipt(alert);
          }
          break;
        case AlertDeliveryAction.notify:
          this.notifyOnce(id, describe(alert));
          this.silence(id);
          break;
        case AlertDeliveryAction.stop:
        case AlertDeliveryAction.silent:
          this.silence(id);
          break;
      }
    }
  }

  /** Stop everything (e.g. when the delivery context tears down). */
  reset(): void {
    for (const id of this.sounding) Native.stopAlarm(String(id));
    this.sounding.clear();
    this.notified.clear();
    this.receipted.clear();
  }

  private notifyOnce(id: number, descriptor: AlertDescriptor): void {
    if (this.notified.has(id)) return;
    Native.showNativeNotification(String(id), descriptor.title, descriptor.body);
    this.notified.add(id);
  }

  private silence(id: number): void {
    if (this.sounding.has(id)) {
      Native.stopAlarm(String(id));
      this.sounding.delete(id);
    }
  }
}
